import type { Cluster, Commitment } from '@solana/web3.js'
// import type { RpcData } from '~/services'
import type { RateLimitOptions } from '~/utils'
import { Connection, PublicKey } from '@solana/web3.js'
import { defineStore } from 'pinia'
import {
  DEFAULT_COMMITMENT,
  DEFAULT_CONFIRM_TIMEOUT,
  DEFAULT_ENDPOINT,
  ENDPOINTS,
} from '~/config'
// import { getRpcList } from '~/services'
import { createRateLimitedFetch, tokenAuthFetchMiddleware } from '~/utils'

const RPC_KEY = 'rpc'
const RPC_LIST_KEY = 'rpc-list'
const DEFAULT_TOKEN_EXPIRY = 5 * 60 * 1000 // 5 minutes

/**
 * Store for managing Solana connection and related settings
 */
export const useConnectionStore = defineStore('connection', () => {
  const state = reactive({
    commitment: DEFAULT_COMMITMENT,
    confirmTransactionInitialTimeout: DEFAULT_CONFIRM_TIMEOUT,
    rpcListLoading: false,
  })

  const rpc = useLocalStorage(RPC_KEY, '')
  const rpcList = useLocalStorage<Endpoint[]>(RPC_LIST_KEY, [])
  const endpoint = computed(() => rpcList.value?.find(e => e.id === rpc.value) ?? DEFAULT_ENDPOINT)
  const cluster = computed(() => endpoint.value?.cluster)
  const network = computed(() => cluster.value?.replace('-beta', ''))

  const stakePoolAddress = computed(() => endpoint.value
    ? new PublicKey(endpoint.value.stakePoolAddress)
    : null,
  )

  const connection = computed(() => {
    const { url, wsEndpoint, rateLimit, getToken } = endpoint.value ?? {}
    return new Connection(url, {
      confirmTransactionInitialTimeout: state.confirmTransactionInitialTimeout,
      wsEndpoint,
      commitment: state.commitment,
      fetch: createRateLimitedFetch(rateLimit),
      fetchMiddleware: getToken ? tokenAuthFetchMiddleware({ tokenExpiry: DEFAULT_TOKEN_EXPIRY, getToken }) : undefined,
    })
  })

  /**
   * Try to load the RPC list from the server and merge with default endpoints
   * Fallback to default endpoints if loading fails
   */
  async function loadRpcList() {
    rpcList.value = ENDPOINTS
    // let currentRpcList: Partial<RpcData>[] = rpcList.value
    // try {
    //   state.rpcListLoading = true
    //   currentRpcList = /* await getRpcList() */[]
    // } finally {
    //   state.rpcListLoading = false
    //   rpcList.value = ENDPOINTS
    //     .map((e) => {
    //       const matchedRpc = currentRpcList.find(r => r?.name === e.name)
    //       return {
    //         ...e,
    //         url: matchedRpc?.url ?? e.url,
    //       }
    //     })
    // }
  }

  function setRpc(_rpc: string) {
    rpc.value = _rpc
  }

  function setCommitment(commitment: Commitment) {
    state.commitment = commitment
  }

  onMounted(() => {
    void loadRpcList()
  })

  return {
    cluster,
    rpcList,
    network,
    endpoint,
    connection,
    stakePoolAddress,

    setRpc,
    setCommitment,
  }
})

export type ExtendedCluster = Cluster | 'localnet'

export type Endpoint = {
  id: string
  name: string
  url: string
  cluster: ExtendedCluster
  stakePoolAddress: string
  wsEndpoint?: string
  rateLimit?: RateLimitOptions
  getToken?: () => Promise<string>
}
